import { toDateSafe } from './dates';
import type { DateInput } from './dates';
import type {
  DiscussionNotificationSettings,
  DiscussionTopic,
  NotificationImmediateChannel,
  NotificationMode
} from './types';
import { normalizeDiscussionCategoryId, valueForDiscussionCategory } from './discussionCategories';

export const DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS: DiscussionNotificationSettings = {
  allDiscussionsMode: 'summary',
  allDiscussionsImmediateChannel: 'email',
  tagModes: {},
  tagImmediateChannels: {},
  topicModes: {},
  topicImmediateChannels: {}
};

export function isNotificationMode(value: unknown): value is NotificationMode {
  return value === 'off' || value === 'immediate' || value === 'summary';
}

export function isNotificationImmediateChannel(value: unknown): value is NotificationImmediateChannel {
  return value === 'email' || value === 'browser' || value === 'both';
}

function normalizeModeMap(value: unknown): Record<string, NotificationMode> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  const out: Record<string, NotificationMode> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const trimmedKey = String(key || '').trim();
    if (!trimmedKey) continue;
    if (!isNotificationMode(raw)) continue;
    out[trimmedKey] = raw;
  }
  return out;
}

function normalizeImmediateChannelMap(value: unknown): Record<string, NotificationImmediateChannel> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  const out: Record<string, NotificationImmediateChannel> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const trimmedKey = String(key || '').trim();
    if (!trimmedKey) continue;
    if (!isNotificationImmediateChannel(raw)) continue;
    out[trimmedKey] = raw;
  }
  return out;
}

export function normalizeDiscussionNotificationSettings(value: unknown): DiscussionNotificationSettings {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

  return {
    allDiscussionsMode: isNotificationMode(raw.allDiscussionsMode)
      ? raw.allDiscussionsMode
      : DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS.allDiscussionsMode,
    allDiscussionsImmediateChannel: isNotificationImmediateChannel(raw.allDiscussionsImmediateChannel)
      ? raw.allDiscussionsImmediateChannel
      : DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS.allDiscussionsImmediateChannel,
    tagModes: normalizeModeMap(raw.tagModes),
    tagImmediateChannels: normalizeImmediateChannelMap(raw.tagImmediateChannels),
    topicModes: normalizeModeMap(raw.topicModes),
    topicImmediateChannels: normalizeImmediateChannelMap(raw.topicImmediateChannels),
    lastViewedAt: raw.lastViewedAt as DateInput | undefined
  };
}

export function topicOverrideMode(
  settings: DiscussionNotificationSettings,
  topicId: string
): NotificationMode | null {
  const id = String(topicId || '').trim();
  if (!id) return null;
  return settings.topicModes[id] ?? null;
}

export function effectiveTopicNotificationMode(
  topic: Pick<DiscussionTopic, 'id' | 'tagIds'>,
  settings: DiscussionNotificationSettings
): NotificationMode {
  const topicOverride = topicOverrideMode(settings, topic.id);
  if (topicOverride === 'immediate' || topicOverride === 'summary') return topicOverride;

  let hasImmediate = false;
  let hasSummary = false;
  let hasOff = false;

  for (const tagId of Array.isArray(topic.tagIds) ? topic.tagIds : []) {
    const mode = valueForDiscussionCategory(settings.tagModes, tagId);
    if (!mode) continue;
    if (mode === 'immediate') hasImmediate = true;
    else if (mode === 'summary') hasSummary = true;
    else if (mode === 'off') hasOff = true;
  }

  if (hasImmediate) return 'immediate';
  if (hasSummary) return 'summary';
  if (hasOff) return 'off';

  return settings.allDiscussionsMode;
}

export function effectiveTopicSummaryMode(
  topic: Pick<DiscussionTopic, 'tagIds'>,
  settings: DiscussionNotificationSettings
): 'summary' | 'off' {
  if (settings.allDiscussionsMode !== 'summary') return 'off';

  for (const tagId of Array.isArray(topic.tagIds) ? topic.tagIds : []) {
    if (valueForDiscussionCategory(settings.tagModes, tagId) === 'off') return 'off';
  }

  return 'summary';
}

export function defaultImmediateChannel(settings: DiscussionNotificationSettings): NotificationImmediateChannel {
  return settings.allDiscussionsImmediateChannel || DEFAULT_DISCUSSION_NOTIFICATION_SETTINGS.allDiscussionsImmediateChannel;
}

export function tagImmediateChannel(settings: DiscussionNotificationSettings, tagId: string): NotificationImmediateChannel {
  const id = String(tagId || '').trim();
  if (!id) return defaultImmediateChannel(settings);
  return settings.tagImmediateChannels[id] ?? defaultImmediateChannel(settings);
}

export function topicImmediateChannel(
  settings: DiscussionNotificationSettings,
  topicId: string
): NotificationImmediateChannel {
  const id = String(topicId || '').trim();
  if (!id) return defaultImmediateChannel(settings);
  return settings.topicImmediateChannels[id] ?? defaultImmediateChannel(settings);
}

export function channelIncludesEmail(channel: NotificationImmediateChannel): boolean {
  return channel === 'email' || channel === 'both';
}

export function channelIncludesBrowser(channel: NotificationImmediateChannel): boolean {
  return channel === 'browser' || channel === 'both';
}

export function notificationLastViewedAtMs(settings: DiscussionNotificationSettings): number {
  const value = toDateSafe(settings.lastViewedAt);
  return value ? value.getTime() : 0;
}
